'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSearchStream } from '@/lib/unified-search/stream';
import { mapAnalysisToClassifiedQuery } from '@/lib/unified-search/mappers';
import {
  initSearchCache,
  putSearchSnapshot,
  evaluateSearch,
  loadSearch,
} from '@/lib/unified-search/search-save-client';
import type { QueueContentTypes, SearchStreamEvent } from '@/lib/unified-search/types';
import type { CompanyEntityData } from '@/components/ui/design-system/entities/company';
import type { ContactEntityData } from '@/components/ui/design-system/entities/contact';
import type { JobEntityData } from '@/components/ui/design-system/entities/job';
import {
  INITIAL_STAGES,
  type ClassifiedQuery,
  type FeedItem,
  type StageState,
} from '@/app/d/search/types';
import type { CardDecision, CompanyResolution, DeckCard, DeckCardKind, DeckCardView, DeckGroup } from '../types';

// ── Round type for multi-round refine tracking ──
// Re-export the canonical SearchLogEntry from search types so the rail panel
// receives the same shape (with id + details) as the regular search feed.
export type { SearchLogEntry } from '@/app/d/search/types';
import type { SearchLogEntry } from '@/app/d/search/types';

export interface SearchRound {
  id: string;
  query: string;
  logs: SearchLogEntry[];
}

const COMPLETED_STAGES: StageState = {
  understanding: 'completed',
  exactMatch: 'completed',
  similarExpansion: 'completed',
  companies: 'completed',
  jobs: 'completed',
  expandedJobs: 'completed',
  contacts: 'completed',
  answer: 'completed',
};

/** Snapshot throttle window — mirrors SearchClient's 3s cadence. */
const SNAPSHOT_THROTTLE_MS = 3000;

const MAX_GROUPS = 60;
/** Cap job-cards per company so one prolific employer can't flood the deck. */
const MAX_JOBS_PER_COMPANY = 6;
/** Cap the job-only tail (jobs we found no contact for) so it can't flood the deck. */
const MAX_JOB_ONLY_CARDS = 30;
/** Resolve companies a few groups ahead of the current one. */
const RESOLVE_AHEAD = 3;

/** Stable key for a job-only card (no contact). */
export function jobCardKeyOf(job: JobEntityData): string {
  return `job::${job.id}`;
}

export interface DeckStats {
  sent: number;
  drafted: number;
  discarded: number;
  applied: number;
}

/** Cards grouped by the decision the user made on them. */
export interface DeckBuckets {
  sent: DeckCard[];
  saved: DeckCard[];
  passed: DeckCard[];
  applied: DeckCard[];
}

/** One contact ready for lazy message generation, bundled with its job context. */
export interface MessageTarget {
  msgKey: string;
  /** Null on job-only targets — the messages hook skips these. */
  contact: ContactEntityData | null;
  company: CompanyEntityData | null;
  companyResolution: CompanyResolution;
  job: JobEntityData | null;
}

/**
 * A single step in the flattened, group-major reading order. Usually a contact;
 * a job-only group contributes ONE 'job' step (contact === null).
 */
export interface FlatContact {
  groupIndex: number;
  contactIndexInGroup: number;
  group: DeckGroup;
  kind: DeckCardKind;
  contact: ContactEntityData | null;
  /** Identity / decision key — the contact key, or `job::<id>` for job cards. */
  contactKey: string;
  msgKey: string;
  companyKey: string;
}

export interface DeckResetOptions {
  resumeText?: string | null;
  context?: string[];
  /** Promote directly to a saved Report on completion (same as /d/search). */
  autoSave?: boolean;
  /** Content-lane scope forwarded to the simple pipeline. */
  contentTypes?: QueueContentTypes;
}

export interface SimpleSearchDeck {
  groups: DeckGroup[];
  /** Index of the group currently on top of the pile. */
  groupIndex: number;
  /** Contact position within the top group. */
  contactIndex: number;
  /** Absolute position in the flattened contact sequence. */
  flatIndex: number;
  /** Total contact steps across all groups. */
  flatTotal: number;
  /** The current contact bundled with its company + job, or undefined when done. */
  currentView: DeckCardView | undefined;
  /** Targets for lazy message generation, in reading order. */
  messageTargets: MessageTarget[];
  /** Current query text (set on reset and on hydrate-from-saved). */
  query: string;
  /** The persisted search id (in the URL), or null before the first search. */
  searchId: string | null;
  /** Decision made on each contact, keyed by contact key. Absent = undecided. */
  decisions: Record<string, CardDecision>;
  /** Decided contacts grouped by decision, for the Sent / Saved / Passed lists. */
  buckets: DeckBuckets;
  getDecision: (key: string) => CardDecision | undefined;
  /** Record a decision on the CURRENT contact and advance to the next undecided one. */
  advance: (decision: CardDecision) => void;
  /** Skip every remaining job-card of the current company. Records no decisions. */
  skipCompany: () => void;
  /** Skip the rest of the current job-card's contacts, moving to the next role. Records no decisions. */
  skipJob: () => void;
  /** Overwrite a specific contact's decision in place (no cursor move). */
  setDecision: (key: string, decision: CardDecision) => void;
  /** Remove a specific contact's decision (undo a pass / roll back a failed send). */
  clearDecision: (key: string) => void;
  /** Step through the contact history WITHOUT recording a decision. */
  goPrev: () => void;
  goNext: () => void;
  canGoPrev: boolean;
  canGoNext: boolean;
  /** Re-surface a specific contact (e.g. tapped from a bucket list). */
  jumpTo: (key: string) => void;
  stages: StageState;
  isStreaming: boolean;
  errored: boolean;
  stats: DeckStats;
  rounds: SearchRound[];
  reset: (query: string, options?: DeckResetOptions) => void;
  /** Refine the current search — appends new groups without clearing decisions. */
  refine: (query: string) => void;
  /** Restore a previously saved search by id (no re-run; same as /d/search). */
  hydrate: (searchId: string) => void;
  /** Wipe everything back to the pristine idle state (no new search). */
  clearAll: () => void;
  /** Accumulate externally-fetched feed items into the deck (dedup by id, in-place update on repeat). */
  injectFeedItems: (items: FeedItem[]) => void;
  /** Load this user's DB-backed suggestion pool into the deck; returns its size + entity→suggestion id map. */
  loadFromPool: (query: string) => Promise<{ count: number; ids: Record<string, string> }>;
}

/** Company lookup — mirrors fetchCompanyData in LazyCompaniesList. */
async function lookupCompany(
  companyName: string,
  signal?: AbortSignal,
): Promise<CompanyEntityData | null> {
  try {
    const response = await fetch('/api/search/company-lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyName }),
      signal,
    });
    if (!response.ok) return null;
    const json = await response.json();
    return json.company ?? null;
  } catch {
    return null;
  }
}

/**
 * Stable per-contact identity for deduping and as the GLOBAL decision key.
 * Falls back to LinkedIn URL or email so a mis-mapped contact degrades to one
 * un-keyed card rather than silently vanishing.
 */
export function contactKeyOf(contact: ContactEntityData): string | null {
  return contact.id || contact.linkedInUrl || contact.email || null;
}

/** Stable company identity — ties a company's job-cards together. */
function companyKeyOf(company: CompanyEntityData | null, name: string | undefined): string {
  return (company?.id || name?.trim().toLowerCase() || 'unknown').toString();
}

/** Per-(job, contact) message cache key, so one person can get a role-specific pitch. */
export function messageKeyFor(group: DeckGroup, contact: ContactEntityData): string {
  const ck = contactKeyOf(contact) ?? 'unknown';
  return `${group.job?.id ?? 'no-job'}::${ck}`;
}

/** Build the decision-ready view for a group's contact at `idx` (or null). */
export function buildCardView(group: DeckGroup, idx: number): DeckCardView | null {
  // Job-only card: no contacts, just an open role to apply to / find someone for.
  if (group.contacts.length === 0 && group.job) {
    return {
      kind: 'job',
      key: jobCardKeyOf(group.job),
      msgKey: jobCardKeyOf(group.job),
      contact: null,
      company: group.company,
      companyResolution: group.companyResolution,
      job: group.job,
      groupKey: group.key,
      companyKey: group.companyKey,
      contactIndexInGroup: 0,
      groupContactCount: 0,
    };
  }
  const contact = group.contacts[idx];
  if (!contact) return null;
  return {
    kind: 'contact',
    key: contactKeyOf(contact) ?? '',
    msgKey: messageKeyFor(group, contact),
    contact,
    company: group.company,
    companyResolution: group.companyResolution,
    job: group.job,
    groupKey: group.key,
    companyKey: group.companyKey,
    contactIndexInGroup: idx,
    groupContactCount: group.contacts.length,
  };
}

/** One-line "City, ST" for a job's primary location. */
export function jobLocationLabel(job: JobEntityData | null): string {
  if (!job) return '';
  const loc = job.locations?.[0];
  if (!loc) return '';
  if (loc.remote) return [loc.city, 'Remote'].filter(Boolean).join(' · ');
  return [loc.city, loc.state].filter(Boolean).join(', ');
}

/**
 * Expand the accumulated feed into (company, job) groups:
 *  - each topMatchesSection match → one group per job (or one company-only
 *    group when it has no jobs); contacts are shared across the company's jobs.
 *  - contactsSection contacts → company-only groups (the fallback tail),
 *    grouped by company, skipping companies already produced above.
 * Contacts are deduped globally by contact key (a person appears once).
 */
function buildGroups(feed: FeedItem[]): DeckGroup[] {
  const groups: DeckGroup[] = [];
  const seenContacts = new Set<string>();
  const seenCompanies = new Set<string>();

  const dedupeContacts = (contacts: ContactEntityData[]): ContactEntityData[] => {
    const out: ContactEntityData[] = [];
    for (const contact of contacts) {
      const ck = contactKeyOf(contact);
      if (!ck || seenContacts.has(ck)) continue;
      seenContacts.add(ck);
      out.push(contact);
    }
    return out;
  };

  // Pass 1 — top matches (company already resolved; may carry jobs).
  for (const item of feed) {
    if (item.type !== 'topMatchesSection') continue;
    for (const match of item.matches) {
      const cKey = companyKeyOf(match.company, match.company?.name);
      const contacts = dedupeContacts(match.contacts);
      if (contacts.length === 0) continue;
      const jobs = (Array.isArray(match.jobs) ? match.jobs : []).slice(0, MAX_JOBS_PER_COMPANY);
      // PRODUCT RULE (job anchoring): a company/outreach card may exist ONLY when
      // it is backed by a concrete open role. A top match with contacts but no
      // job violates that, so drop it here — a defensive net mirroring the
      // pipeline's `enrichClaimed` no-job guard, in case a job-less match ever
      // reaches the deck (stale feed, future path). The live pipeline never emits
      // one, and the pool feed re-anchors before this point, so this only kills
      // genuine no-job cards.
      if (jobs.length === 0) continue;
      seenCompanies.add(cKey);
      for (const job of jobs) {
        groups.push({
          key: `${cKey}::${job.id}`,
          companyKey: cKey,
          company: match.company,
          companyResolution: 'ready',
          job,
          contacts,
          source: 'topMatch',
        });
      }
    }
  }

  // Pass 2 — standalone contacts → company-only groups (fallback tail).
  const byCompany = new Map<string, { name?: string; contacts: ContactEntityData[] }>();
  const order: string[] = [];
  for (const item of feed) {
    if (item.type !== 'contactsSection') continue;
    for (const contact of item.contacts) {
      const ck = contactKeyOf(contact);
      if (!ck || seenContacts.has(ck)) continue;
      const name = contact.company?.name?.trim();
      const cKey = companyKeyOf(null, name);
      if (seenCompanies.has(cKey)) continue;
      seenContacts.add(ck);
      if (!byCompany.has(cKey)) {
        byCompany.set(cKey, { name, contacts: [] });
        order.push(cKey);
      }
      byCompany.get(cKey)!.contacts.push(contact);
    }
  }
  for (const cKey of order) {
    const entry = byCompany.get(cKey)!;
    groups.push({
      key: `${cKey}::no-job`,
      companyKey: cKey,
      company: null,
      companyResolution: entry.name ? 'pending' : 'failed',
      job: null,
      contacts: entry.contacts,
      source: 'contactsSection',
    });
  }

  // Pass 3 — leftover jobs we found NO contact for → job-only cards (the tail).
  // Built straight from the job (company name/logo + apply URL); no contact, so
  // these render the apply / contact-hiring-manager action block instead.
  const seenJobCards = new Set<string>();
  let jobOnlyCount = 0;
  for (const item of feed) {
    if (item.type !== 'jobsSection') continue;
    if (jobOnlyCount >= MAX_JOB_ONLY_CARDS) break;
    for (const entry of item.jobs) {
      if (jobOnlyCount >= MAX_JOB_ONLY_CARDS) break;
      const job = entry.job;
      if (!job?.id || seenJobCards.has(job.id)) continue;
      const name = job.company?.name?.trim();
      const cKey = companyKeyOf(null, name);
      // A company already surfaced (with contacts) covers this job — skip it.
      if (seenCompanies.has(cKey)) continue;
      seenJobCards.add(job.id);
      jobOnlyCount += 1;
      const company = job.company
        ? ({ id: job.company.id, name: job.company.name, logoUrl: job.company.logoUrl } as CompanyEntityData)
        : null;
      groups.push({
        key: `${cKey}::${jobCardKeyOf(job)}`,
        companyKey: cKey,
        company,
        companyResolution: 'failed',
        job,
        contacts: [],
        source: 'jobsSection',
      });
    }
  }

  return groups.slice(0, MAX_GROUPS);
}

/** Flatten groups into the group-major reading order (contacts, then job-only). */
export function flattenGroups(groups: DeckGroup[]): FlatContact[] {
  const flat: FlatContact[] = [];
  groups.forEach((group, groupIndex) => {
    // Job-only group → a single 'job' step (no contact to send to).
    if (group.contacts.length === 0 && group.job) {
      flat.push({
        groupIndex,
        contactIndexInGroup: 0,
        group,
        kind: 'job',
        contact: null,
        contactKey: jobCardKeyOf(group.job),
        msgKey: jobCardKeyOf(group.job),
        companyKey: group.companyKey,
      });
      return;
    }
    group.contacts.forEach((contact, contactIndexInGroup) => {
      const ck = contactKeyOf(contact);
      if (!ck) return;
      flat.push({
        groupIndex,
        contactIndexInGroup,
        group,
        kind: 'contact',
        contact,
        contactKey: ck,
        msgKey: messageKeyFor(group, contact),
        companyKey: group.companyKey,
      });
    });
  });
  return flat;
}

export function useSimpleSearchDeck(): SimpleSearchDeck {
  const [groups, setGroups] = useState<DeckGroup[]>([]);
  // Single cursor over the flattened contact sequence; group/contact indices
  // are derived from it so the two levels never drift.
  const [flatIndex, setFlatIndex] = useState(0);
  const [decisions, setDecisions] = useState<Record<string, CardDecision>>({});
  const decisionsRef = useRef<Record<string, CardDecision>>({});
  // Furthest flat index reached — bounds history nav and locks the streaming
  // merge so revisiting never reshuffles already-seen groups.
  const [maxReached, setMaxReached] = useState(0);
  const [stages, setStages] = useState<StageState>({ ...INITIAL_STAGES });
  const [isStreaming, setIsStreaming] = useState(false);
  const [errored, setErrored] = useState(false);
  const [query, setQuery] = useState('');
  const [searchId, setSearchId] = useState<string | null>(null);
  const [rounds, setRounds] = useState<SearchRound[]>([]);
  const roundsRef = useRef<SearchRound[]>([]);
  const activeRoundRef = useRef<string | null>(null);

  const feedRef = useRef<FeedItem[]>([]);
  const feedIdsRef = useRef<Set<string>>(new Set());
  const abortRef = useRef<(() => void) | null>(null);
  const companyCacheRef = useRef<Map<string, CompanyEntityData | null>>(new Map());
  const companyInFlightRef = useRef<Set<string>>(new Set());
  // Synchronous mirrors readable from stream callbacks + nav helpers.
  const groupsRef = useRef<DeckGroup[]>([]);
  const flatRef = useRef<FlatContact[]>([]);
  const flatIndexRef = useRef(0);
  const maxReachedRef = useRef(0);
  // Furthest GROUP reached — used to lock the streaming merge at group level.
  const maxGroupReachedRef = useRef(0);
  // Persistence mirrors.
  const searchIdRef = useRef<string | null>(null);
  const queryRef = useRef<string>('');
  const classifiedRef = useRef<ClassifiedQuery | null>(null);
  const stagesRef = useRef<StageState>({ ...INITIAL_STAGES });
  const autoSaveRef = useRef<boolean>(false);
  const lastSnapshotTimeRef = useRef(0);
  const snapshotTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const snapshotPendingRef = useRef(false);
  const loadTokenRef = useRef(0);

  // Keep flat sequence + its ref in sync with groups. The refs back the
  // user-triggered nav callbacks (advance / skip / arrows), which always fire
  // after commit, so syncing in an effect is safe.
  const flat = useMemo(() => flattenGroups(groups), [groups]);
  useEffect(() => {
    flatRef.current = flat;
    groupsRef.current = groups;
  }, [flat, groups]);

  const applyDecision = useCallback((key: string, decision: CardDecision) => {
    decisionsRef.current = { ...decisionsRef.current, [key]: decision };
    setDecisions(decisionsRef.current);
  }, []);

  const setIndex = useCallback((next: number) => {
    flatIndexRef.current = next;
    setFlatIndex(next);
    if (next > maxReachedRef.current) {
      maxReachedRef.current = next;
      setMaxReached(next);
    }
    const g = flatRef.current[next]?.groupIndex ?? groupsRef.current.length;
    if (g > maxGroupReachedRef.current) maxGroupReachedRef.current = g;
  }, []);

  /** First index ≥ from whose contact has no decision yet; flat.length if none. */
  const findNextUndecided = useCallback(
    (from: number, decisionMap: Record<string, CardDecision>): number => {
      const f = flatRef.current;
      for (let i = Math.max(0, from); i < f.length; i++) {
        if (!decisionMap[f[i].contactKey]) return i;
      }
      return f.length;
    },
    [],
  );

  // ── Snapshot persistence (throttled, mirrors SearchClient's cadence). ──
  const flushSnapshot = useCallback(() => {
    const sid = searchIdRef.current;
    if (!sid) return;
    lastSnapshotTimeRef.current = Date.now();
    snapshotPendingRef.current = false;
    putSearchSnapshot(sid, {
      query: queryRef.current,
      classified: classifiedRef.current,
      stages: stagesRef.current,
      feed: feedRef.current,
    });
  }, []);

  const scheduleSnapshot = useCallback(() => {
    if (!searchIdRef.current) return;
    snapshotPendingRef.current = true;
    const elapsed = Date.now() - lastSnapshotTimeRef.current;
    if (elapsed >= SNAPSHOT_THROTTLE_MS) {
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
      flushSnapshot();
    } else if (!snapshotTimerRef.current) {
      snapshotTimerRef.current = setTimeout(() => {
        snapshotTimerRef.current = null;
        if (snapshotPendingRef.current) flushSnapshot();
      }, SNAPSHOT_THROTTLE_MS - elapsed);
    }
  }, [flushSnapshot]);

  const mergeFeedIntoGroups = useCallback(() => {
    const built = buildGroups(feedRef.current);
    setGroups((prev) => {
      if (prev.length === 0) return built;
      // Lock the groups the user has already reached so a late-arriving feed
      // section can't reshuffle the order under the cursor; re-derive only the
      // not-yet-seen tail from the freshly-built priority order, preserving each
      // surviving group's company-resolution progress by key.
      const lockCount = Math.min(maxGroupReachedRef.current + 1, prev.length);
      const locked = prev.slice(0, lockCount);
      const lockedKeys = new Set(locked.map((g) => g.key));
      const prevByKey = new Map(prev.map((g) => [g.key, g]));
      const tail = built
        .filter((group) => !lockedKeys.has(group.key))
        .map((group) => prevByKey.get(group.key) ?? group);
      return [...locked, ...tail].slice(0, MAX_GROUPS);
    });
  }, []);

  const onEvent = useCallback(
    (event: SearchStreamEvent) => {
      switch (event.type) {
        case 'classified':
          classifiedRef.current = mapAnalysisToClassifiedQuery(queryRef.current, event.analysis);
          scheduleSnapshot();
          break;
        case 'stage':
          setStages((prev) => {
            const next = { ...prev, [event.key]: event.status };
            stagesRef.current = next;
            return next;
          });
          scheduleSnapshot();
          break;
        case 'log': {
          const roundId = activeRoundRef.current;
          if (roundId) {
            const logEntry: SearchLogEntry = {
              id: `log-${event.key}-${Date.now()}`,
              stageKey: event.key,
              message: event.message,
              details: event.details,
            };
            setRounds((prev) => {
              const next = prev.map((r) =>
                r.id === roundId ? { ...r, logs: [...r.logs, logEntry] } : r,
              );
              roundsRef.current = next;
              return next;
            });
          }
          break;
        }
        case 'partial': {
          let changed = false;
          for (const item of event.items) {
            if (!feedIdsRef.current.has(item.id)) {
              feedIdsRef.current.add(item.id);
              feedRef.current.push(item);
              changed = true;
            } else {
              const idx = feedRef.current.findIndex((f) => f.id === item.id);
              if (idx >= 0) {
                feedRef.current[idx] = item;
                changed = true;
              }
            }
          }
          if (changed) {
            mergeFeedIntoGroups();
            scheduleSnapshot();
          }
          break;
        }
        case 'error':
          setErrored(true);
          setIsStreaming(false);
          break;
        case 'done':
          setIsStreaming(false);
          if (snapshotTimerRef.current) {
            clearTimeout(snapshotTimerRef.current);
            snapshotTimerRef.current = null;
          }
          flushSnapshot();
          {
            const sid = searchIdRef.current;
            if (sid) {
              void evaluateSearch({
                searchId: sid,
                query: queryRef.current,
                classified: classifiedRef.current,
                feed: feedRef.current,
                autoSave: autoSaveRef.current,
              }).then((res) => {
                if (res?.promoted && res.searchId && res.searchId !== searchIdRef.current) {
                  searchIdRef.current = res.searchId;
                  setSearchId(res.searchId);
                  if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.set('searchId', res.searchId);
                    window.history.replaceState({}, '', url.toString());
                  }
                }
              });
            }
          }
          break;
        default:
          break;
      }
    },
    [mergeFeedIntoGroups, scheduleSnapshot, flushSnapshot],
  );

  const clearForNewRun = useCallback(() => {
    loadTokenRef.current += 1;
    abortRef.current?.();
    if (snapshotTimerRef.current) {
      clearTimeout(snapshotTimerRef.current);
      snapshotTimerRef.current = null;
    }
    feedRef.current = [];
    feedIdsRef.current = new Set();
    companyCacheRef.current = new Map();
    companyInFlightRef.current = new Set();
    classifiedRef.current = null;
    snapshotPendingRef.current = false;
    lastSnapshotTimeRef.current = 0;
    setGroups([]);
    groupsRef.current = [];
    flatRef.current = [];
    flatIndexRef.current = 0;
    maxReachedRef.current = 0;
    maxGroupReachedRef.current = 0;
    setFlatIndex(0);
    setMaxReached(0);
    decisionsRef.current = {};
    setDecisions({});
    setErrored(false);
    roundsRef.current = [];
    activeRoundRef.current = null;
    setRounds([]);
  }, []);

  const makeId = (prefix: string): string =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${prefix}-${Date.now()}`;

  // Accumulate externally-fetched feed items into the deck, mirroring the
  // 'partial' stream branch: dedup by id, update in place on a repeat, then
  // rebuild the groups once if anything changed.
  const injectFeedItems = useCallback(
    (items: FeedItem[]) => {
      let changed = false;
      for (const item of items) {
        if (!feedIdsRef.current.has(item.id)) {
          feedIdsRef.current.add(item.id);
          feedRef.current.push(item);
          changed = true;
        } else {
          const idx = feedRef.current.findIndex((f) => f.id === item.id);
          if (idx >= 0) {
            feedRef.current[idx] = item;
            changed = true;
          }
        }
      }
      if (changed) mergeFeedIntoGroups();
    },
    [mergeFeedIntoGroups],
  );

  // Load this user's DB-backed suggestion pool into the deck (no live stream).
  // Resets state like a fresh run, sets the query, and injects the returned feed.
  const loadFromPool = useCallback(
    async (nextQuery: string): Promise<{ count: number; ids: Record<string, string> }> => {
      clearForNewRun();
      queryRef.current = nextQuery;
      setQuery(nextQuery);
      const res = await fetch('/api/suggestions?limit=75', { cache: 'no-store' });
      if (!res.ok) return { count: 0, ids: {} };
      const data = (await res.json()) as { feed: FeedItem[]; ids: Record<string, string>; count: number };
      if (data.count > 0) injectFeedItems(data.feed);
      return { count: data.count, ids: data.ids };
    },
    [clearForNewRun, injectFeedItems],
  );

  const reset = useCallback(
    (nextQuery: string, options?: DeckResetOptions) => {
      clearForNewRun();
      const round: SearchRound = { id: makeId('round'), query: nextQuery, logs: [] };
      activeRoundRef.current = round.id;
      roundsRef.current = [round];
      setRounds([round]);
      const id = makeId('ss');
      searchIdRef.current = id;
      setSearchId(id);
      queryRef.current = nextQuery;
      setQuery(nextQuery);
      autoSaveRef.current = Boolean(options?.autoSave);
      stagesRef.current = { ...INITIAL_STAGES };
      setStages({ ...INITIAL_STAGES });
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.searchParams.set('searchId', id);
        window.history.replaceState({}, '', url.toString());
      }
      initSearchCache(id, nextQuery);
      setIsStreaming(true);
      abortRef.current = createSearchStream(
        { query: nextQuery, resumeText: options?.resumeText ?? undefined, context: options?.context, mode: 'simple', contentTypes: options?.contentTypes },
        onEvent,
      );
    },
    [clearForNewRun, onEvent],
  );

  const refine = useCallback(
    (refineQuery: string) => {
      abortRef.current?.();
      const context = roundsRef.current.map((r) => r.query);
      const round: SearchRound = { id: makeId('round'), query: refineQuery, logs: [] };
      activeRoundRef.current = round.id;
      setRounds((prev) => {
        const next = [...prev, round];
        roundsRef.current = next;
        return next;
      });
      stagesRef.current = { ...INITIAL_STAGES };
      setStages({ ...INITIAL_STAGES });
      setIsStreaming(true);
      setErrored(false);
      abortRef.current = createSearchStream({ query: refineQuery, context, mode: 'simple' }, onEvent);
    },
    [onEvent],
  );

  const hydrate = useCallback(
    (id: string) => {
      clearForNewRun();
      const token = loadTokenRef.current;
      searchIdRef.current = id;
      setSearchId(id);
      autoSaveRef.current = false;
      setIsStreaming(true);
      void loadSearch(id).then((data) => {
        if (loadTokenRef.current !== token) return;
        if (!data) {
          setErrored(true);
          setIsStreaming(false);
          return;
        }
        queryRef.current = data.query;
        setQuery(data.query);
        classifiedRef.current = data.classified;
        feedRef.current = data.feed;
        feedIdsRef.current = new Set(data.feed.map((item) => item.id));
        const restored = Object.fromEntries(
          Object.entries({ ...COMPLETED_STAGES, ...(data.stages ?? {}) }).map(([k, v]) =>
            v === 'running' || v === 'pending' ? [k, 'completed'] : [k, v],
          ),
        ) as StageState;
        stagesRef.current = restored;
        setStages(restored);
        setGroups(buildGroups(feedRef.current));
        setIsStreaming(false);
      });
    },
    [clearForNewRun],
  );

  // Tear everything down to the pristine, pre-search idle state. Unlike reset()
  // (which immediately kicks off a new stream), this leaves the deck empty so the
  // page falls back to its hero/composer view — used when the user clicks the
  // Simple Search tab to start over.
  const clearAll = useCallback(() => {
    clearForNewRun();
    queryRef.current = '';
    setQuery('');
    searchIdRef.current = null;
    setSearchId(null);
    autoSaveRef.current = false;
    stagesRef.current = { ...INITIAL_STAGES };
    setStages({ ...INITIAL_STAGES });
    setIsStreaming(false);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.searchParams.has('searchId')) {
        url.searchParams.delete('searchId');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [clearForNewRun]);

  // Abort + cleanup on unmount.
  useEffect(
    () => () => {
      loadTokenRef.current += 1;
      abortRef.current?.();
      if (snapshotTimerRef.current) clearTimeout(snapshotTimerRef.current);
    },
    [],
  );

  // Lazy company resolution for the upcoming window of GROUPS (company-only
  // fallback groups arrive with just a name; fill in logo/description).
  const currentGroupIndex = flat[flatIndex]?.groupIndex ?? groups.length;
  useEffect(() => {
    const windowGroups = groups.slice(currentGroupIndex, currentGroupIndex + RESOLVE_AHEAD + 1);
    for (const group of windowGroups) {
      if (group.companyResolution !== 'pending') continue;
      const name = group.company?.name?.trim() ?? group.contacts[0]?.company?.name?.trim();
      if (!name) continue;
      const keyLower = name.toLowerCase();

      const apply = (data: CompanyEntityData | null) => {
        setGroups((prev) =>
          prev.map((g) =>
            g.companyResolution === 'pending' && g.companyKey === group.companyKey
              ? { ...g, company: data, companyResolution: data ? 'ready' : 'failed' }
              : g,
          ),
        );
      };

      if (companyCacheRef.current.has(keyLower)) {
        apply(companyCacheRef.current.get(keyLower) ?? null);
        continue;
      }
      if (companyInFlightRef.current.has(keyLower)) continue;
      companyInFlightRef.current.add(keyLower);
      void lookupCompany(name).then((data) => {
        companyCacheRef.current.set(keyLower, data);
        companyInFlightRef.current.delete(keyLower);
        apply(data);
      });
    }
  }, [groups, currentGroupIndex]);

  const advance = useCallback(
    (decision: CardDecision) => {
      const cur = flatRef.current[flatIndexRef.current];
      let nextDecisions = decisionsRef.current;
      if (cur) {
        nextDecisions = { ...decisionsRef.current, [cur.contactKey]: decision };
        decisionsRef.current = nextDecisions;
        setDecisions(nextDecisions);
      }
      setIndex(findNextUndecided(flatIndexRef.current + 1, nextDecisions));
    },
    [findNextUndecided, setIndex],
  );

  const skipCompany = useCallback(() => {
    const cur = flatRef.current[flatIndexRef.current];
    if (!cur) return;
    const { companyKey } = cur;
    let i = flatIndexRef.current + 1;
    const f = flatRef.current;
    while (i < f.length && f[i].companyKey === companyKey) i++;
    setIndex(findNextUndecided(i, decisionsRef.current));
  }, [findNextUndecided, setIndex]);

  // Skip the current ROLE rather than the whole company: advance past the
  // remaining contacts of this (company, job) group only, landing on the next
  // group — which may be another open role at the same company. When the
  // company has a single job this naturally behaves like skipCompany.
  const skipJob = useCallback(() => {
    const cur = flatRef.current[flatIndexRef.current];
    if (!cur) return;
    const groupKey = cur.group.key;
    let i = flatIndexRef.current + 1;
    const f = flatRef.current;
    while (i < f.length && f[i].group.key === groupKey) i++;
    setIndex(findNextUndecided(i, decisionsRef.current));
  }, [findNextUndecided, setIndex]);

  const setDecisionExternal = useCallback(
    (key: string, decision: CardDecision) => applyDecision(key, decision),
    [applyDecision],
  );

  const clearDecision = useCallback((key: string) => {
    if (!(key in decisionsRef.current)) return;
    const next = { ...decisionsRef.current };
    delete next[key];
    decisionsRef.current = next;
    setDecisions(next);
  }, []);

  const goPrev = useCallback(() => {
    setIndex(Math.max(0, flatIndexRef.current - 1));
  }, [setIndex]);

  const goNext = useCallback(() => {
    const ceiling = Math.min(maxReachedRef.current, flatRef.current.length - 1);
    setIndex(Math.min(ceiling, flatIndexRef.current + 1));
  }, [setIndex]);

  const jumpTo = useCallback((key: string) => {
    const idx = flatRef.current.findIndex((f) => f.contactKey === key);
    if (idx < 0) return;
    flatIndexRef.current = idx;
    setFlatIndex(idx);
    if (idx > maxReachedRef.current) {
      maxReachedRef.current = idx;
      setMaxReached(idx);
    }
  }, []);

  const getDecision = useCallback((key: string) => decisions[key], [decisions]);

  // Decided contacts → bucket lists, deduped by contact key (first occurrence).
  const buckets = useMemo<DeckBuckets>(() => {
    const sent: DeckCard[] = [];
    const saved: DeckCard[] = [];
    const passed: DeckCard[] = [];
    const applied: DeckCard[] = [];
    const seen = new Set<string>();
    for (const group of groups) {
      // Job-only group → one job card, bucketed by its decision (pass / save).
      if (group.contacts.length === 0 && group.job) {
        const key = jobCardKeyOf(group.job);
        if (seen.has(key)) continue;
        const decision = decisions[key];
        if (!decision) continue;
        seen.add(key);
        const card: DeckCard = {
          kind: 'job',
          key,
          contact: null,
          job: group.job,
          company: group.company,
          companyResolution: group.companyResolution,
          source: group.source,
        };
        if (decision === 'draft' || decision === 'saved') saved.push(card);
        else if (decision === 'discarded') passed.push(card);
        else if (decision === 'applied') applied.push(card);
        continue;
      }
      for (const contact of group.contacts) {
        const ck = contactKeyOf(contact);
        if (!ck || seen.has(ck)) continue;
        const decision = decisions[ck];
        if (!decision) continue;
        seen.add(ck);
        const card: DeckCard = {
          kind: 'contact',
          key: ck,
          contact,
          company: group.company,
          companyResolution: group.companyResolution,
          source: group.source,
        };
        if (decision === 'sent') sent.push(card);
        else if (decision === 'draft' || decision === 'saved') saved.push(card);
        else if (decision === 'discarded') passed.push(card);
        else if (decision === 'applied') applied.push(card);
      }
    }
    return { sent, saved, passed, applied };
  }, [groups, decisions]);

  const stats = useMemo<DeckStats>(
    () => ({
      sent: buckets.sent.length,
      drafted: buckets.saved.length,
      discarded: buckets.passed.length,
      applied: buckets.applied.length,
    }),
    [buckets],
  );

  const messageTargets = useMemo<MessageTarget[]>(
    () =>
      flat.map((f) => ({
        msgKey: f.msgKey,
        contact: f.contact,
        company: f.group.company,
        companyResolution: f.group.companyResolution,
        job: f.group.job,
      })),
    [flat],
  );

  const currentFlat = flat[flatIndex];
  const currentView = useMemo<DeckCardView | undefined>(() => {
    if (!currentFlat) return undefined;
    return {
      kind: currentFlat.kind,
      key: currentFlat.contactKey,
      msgKey: currentFlat.msgKey,
      contact: currentFlat.contact,
      company: currentFlat.group.company,
      companyResolution: currentFlat.group.companyResolution,
      job: currentFlat.group.job,
      groupKey: currentFlat.group.key,
      companyKey: currentFlat.companyKey,
      contactIndexInGroup: currentFlat.contactIndexInGroup,
      groupContactCount: currentFlat.group.contacts.length,
    };
  }, [currentFlat]);

  const ceiling = Math.min(maxReached, flat.length - 1);

  return {
    groups,
    groupIndex: currentFlat?.groupIndex ?? groups.length,
    contactIndex: currentFlat?.contactIndexInGroup ?? 0,
    flatIndex,
    flatTotal: flat.length,
    currentView,
    messageTargets,
    query,
    searchId,
    decisions,
    buckets,
    getDecision,
    advance,
    skipCompany,
    skipJob,
    setDecision: setDecisionExternal,
    clearDecision,
    goPrev,
    goNext,
    canGoPrev: flatIndex > 0,
    canGoNext: flatIndex < ceiling,
    jumpTo,
    stages,
    isStreaming,
    errored,
    rounds,
    stats,
    reset,
    refine,
    hydrate,
    clearAll,
    injectFeedItems,
    loadFromPool,
  };
}
